import type { CurrentUser } from '@/types';

/** Actions accordables par module (la suppression reste réservée à l'ADMIN/propriétaire). */
export const ACTIONS = ['consulter', 'ajouter', 'modifier', 'archiver'] as const;
export type Action = (typeof ACTIONS)[number];
export const ACTION_LABEL: Record<Action, string> = {
  consulter: 'Consulter', ajouter: 'Ajouter', modifier: 'Modifier', archiver: 'Archiver',
};

/** Modules soumis aux permissions (alignés sur les routers gardés côté backend). */
export const MODULES: { key: string; label: string }[] = [
  { key: 'tiers', label: 'Clients / Fournisseurs' },
  { key: 'sous_traitants', label: 'Sous-traitants' },
  { key: 'appels_offres', label: "Appels d'offres" },
  { key: 'crm', label: 'CRM (pipeline commercial)' },
  { key: 'chantiers', label: 'Chantiers (terrain)' },
  { key: 'controle_couts', label: 'Contrôle des coûts (finance chantier)' },
  { key: 'situations', label: 'Situations de travaux' },
  { key: 'depenses', label: 'Dépenses' },
  { key: 'comptabilite', label: 'Comptabilité SCF' },
  { key: 'liasse', label: 'Liasse fiscale' },
  { key: 'fiscalite', label: 'Fiscalité (G50)' },
  { key: 'tresorerie', label: 'Trésorerie' },
  { key: 'cheques', label: 'Chèques' },
  { key: 'engins', label: 'Parc & matériel' },
  { key: 'rh', label: 'RH & paie' },
  { key: 'pointage', label: 'Pointages (chef de chantier)' },
  { key: 'hse', label: 'HSE (sécurité & environnement)' },
  { key: 'achats', label: 'Achats' },
  { key: 'stock', label: 'Stock' },
  { key: 'soumissions', label: 'Soumissions & documents' },
];

/** Chemin de navigation → module(s) de permission (les chemins absents ne sont pas gardés).
 *  Une valeur tableau = visible si l'utilisateur a « consulter » sur AU MOINS UN des modules. */
const NAV_MODULE: Record<string, string | string[]> = {
  '/clients': 'tiers',
  '/fournisseurs': 'tiers',
  '/sous-traitants': 'sous_traitants',
  '/appels-offres': 'appels_offres',
  '/crm': 'crm',
  '/chantiers': 'chantiers',
  '/couts': 'controle_couts',
  '/situations': 'situations',
  '/depenses': 'depenses',
  '/factures': 'achats',
  '/devis': 'achats',
  '/comptabilite': 'comptabilite',
  '/immobilisations': 'comptabilite',
  '/liasse': 'liasse',
  '/fiscalite': 'fiscalite',
  '/tresorerie': 'tresorerie',
  '/cheques': 'cheques',
  '/engins': 'engins',
  '/rh': ['rh', 'pointage'],
  '/hse': 'hse',
  '/achats': 'achats',
  '/stock': 'stock',
  '/documents-entreprise': 'soumissions',
};

/** Tableaux de bord soumis à une permission dédiée (assignable par l'admin, par utilisateur). */
export type DashboardKind = 'patron' | 'operationnel';
export const DASHBOARDS: { key: DashboardKind; permKey: string; label: string; description: string }[] = [
  { key: 'patron', permKey: 'dashboard_patron', label: 'Tableau de bord Patron', description: 'Vue financière et stratégique (trésorerie, CA, marges, créances…).' },
  { key: 'operationnel', permKey: 'dashboard_operationnel', label: 'Tableau de bord Opérationnel', description: 'Vue terrain, sans aucune donnée financière (chantiers, effectifs, engins…).' },
];

/**
 * Découpage des modules en sous-modules (sous-permissions hiérarchiques `module.sousmodule`).
 * SOURCE UNIQUE côté front — miroir de `app/core/permissions.py` (backend).
 * Pour ajouter un module au découpage : déclarer ses sous-modules ICI ; la sous-grille dépliable
 * (UtilisateursTab) et le masquage d'onglets s'en servent. Aucune migration de schéma.
 */
export const SUBMODULES: Record<string, { key: string; label: string }[]> = {
  rh: [
    // Opérationnel (délégable à un assistant)
    { key: 'rh.employes', label: 'Employés' },
    { key: 'rh.pointages', label: 'Pointages' },
    { key: 'rh.conges', label: 'Congés & absences' },
    { key: 'rh.visites_medicales', label: 'Visites médicales' },
    // Confidentiel (réservé direction)
    { key: 'rh.contrats', label: 'Contrats' },
    { key: 'rh.paie', label: 'Calcul de paie' },
    { key: 'rh.fiches_paie', label: 'Fiches de paie' },
    { key: 'rh.avances', label: 'Avances sur salaire' },
  ],
  engins: [
    // Montants des locations (tarifs, brut, net, avances, facturation). Sans ce droit, on pointe
    // et on consulte les pointages mais AUCUN montant n'est renvoyé par le serveur.
    { key: 'engins.tarifs', label: 'Tarifs & montants de location' },
  ],
};

/**
 * Vrai si l'utilisateur a l'action sur le module (ADMIN = propriétaire, tout autorisé).
 * Clé hiérarchique `module.sousmodule` : si la sous-clé n'est pas matérialisée, elle HÉRITE
 * des actions du module parent (défaut additif — miroir de `effective_actions` côté serveur).
 */
export function can(user: CurrentUser | undefined | null, module: string, action: Action): boolean {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;
  const perms = user.permissions ?? {};
  if (module in perms) return !!perms[module]?.includes(action);
  if (module.includes('.')) return !!perms[module.split('.')[0]]?.includes(action);
  return false;
}

/** Vrai si l'utilisateur peut voir le tableau de bord donné (ADMIN = les deux). */
export function canDashboard(user: CurrentUser | undefined | null, kind: DashboardKind): boolean {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;
  return !!user.permissions?.[`dashboard_${kind}`]?.includes('consulter');
}

/** Vrai si l'entrée de menu est visible (consulter sur son/ses module(s), ou pas de module gardé). */
export function canViewNav(user: CurrentUser | undefined | null, to: string): boolean {
  const module = NAV_MODULE[to];
  if (!module) return true; // tableau de bord, paramètres, corbeille, sauvegarde : gérés par rôle
  const modules = Array.isArray(module) ? module : [module];
  return modules.some((m) => can(user, m, 'consulter'));
}

/**
 * Vrai si l'utilisateur peut voir les montants de location (miroir de `peut_voir_tarifs` serveur).
 * `engins.tarifs` explicite → tranche ; sinon le chef de chantier n'a PAS les tarifs par défaut
 * (l'héritage additif les lui donnerait sinon dès qu'il a accès au module « engins »).
 */
export function peutVoirTarifs(user: CurrentUser | undefined | null): boolean {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;
  const perms = user.permissions ?? {};
  if ('engins.tarifs' in perms) return !!perms['engins.tarifs']?.includes('consulter');
  if (user.role === 'CHEF_CHANTIER') return false;
  return can(user, 'engins', 'consulter');
}
